import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const BASE = import.meta.env.VITE_API_BASE_URL || "";

type Step = "email" | "otp" | "sending" | "verifying";

export function Login() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [sessionToken, setSessionToken] = useState("");
  const [error, setError] = useState("");

  // Auto-verify from magic link URL (?session_id=...&code=...)
  useEffect(() => {
    const sid = searchParams.get("session_id");
    const code = searchParams.get("code");
    if (sid && code) {
      setSessionId(sid);
      setOtp(code);
      setStep("verifying");
      verifyCode(sid, code);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError("");
    setStep("sending");
    try {
      const resp = await fetch(`${BASE}/api/auth/magic-link/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `Errore ${resp.status}`);
      setSessionId(data.session_id);
      setSessionToken(data.session_token);
      setStep("otp");
    } catch (err) {
      setError((err as Error).message);
      setStep("email");
    }
  };

  const verifyCode = async (sid?: string, code?: string) => {
    const finalSid = sid || sessionId;
    const finalCode = code || otp;
    if (!finalSid || !finalCode) return;
    setError("");
    setStep("verifying");
    try {
      const resp = await fetch(`${BASE}/api/auth/magic-link/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: finalSid,
          session_token: sessionToken,
          code: finalCode,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `Errore ${resp.status}`);
      localStorage.setItem("trustycert_token", data.token);
      localStorage.setItem("trustycert_user", JSON.stringify(data.user));
      window.location.href = "/";
    } catch (err) {
      setError((err as Error).message);
      setStep("otp");
    }
  };

  const handleVerifyOtp = (e: React.FormEvent) => {
    e.preventDefault();
    verifyCode();
  };

  const busy = step === "sending" || step === "verifying";

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      minHeight: "100vh", fontFamily: "var(--font-family)", background: "#f0f0f0",
    }}>
      <div style={{
        background: "#002855", borderRadius: 12,
        boxShadow: "0 4px 24px rgba(0,40,85,0.25)",
        padding: "2.5rem 2rem 2rem", width: "100%", maxWidth: 420,
        display: "flex", flexDirection: "column", alignItems: "center", gap: "1.25rem",
      }}>
        {/* Brand — Tinexta + Trusty */}
        <img src="/tinexta-logo-white.svg" alt="Tinexta InfoCert" style={{ height: 28 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src="/trusty-icon-white.svg" alt="" style={{ height: 32, width: 32 }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: "1.1rem", color: "#fff", lineHeight: 1.2 }}>
              TrustyCERT
            </div>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "rgba(255,255,255,0.6)" }}>
              Firma Digitale Remota
            </div>
          </div>
        </div>

        <hr style={{ width: "100%", border: "none", borderTop: "1px solid rgba(255,255,255,0.15)", margin: "0.25rem 0" }} />

        {/* Email step */}
        {(step === "email" || step === "sending") && (
          <>
            <p style={{ margin: 0, color: "rgba(255,255,255,0.8)", fontSize: "var(--font-size-base)", textAlign: "center" }}>
              Inserisci la tua email per accedere
            </p>
            <form onSubmit={handleStartLogin} style={{ display: "flex", flexDirection: "column", gap: "0.75rem", width: "100%" }}>
              <input
                className="form-input"
                type="email"
                placeholder="nome@azienda.it"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                disabled={busy}
              />
              <button type="submit" style={{
                width: "100%", padding: "0.75rem", borderRadius: 8, border: "none",
                background: "#fff", color: "#002855", fontWeight: 600, fontSize: "var(--font-size-base)",
                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                opacity: busy || !email.trim() ? 0.6 : 1,
              }} disabled={busy || !email.trim()}>
                {step === "sending" ? (
                  <><span className="spinner" /> Invio in corso…</>
                ) : (
                  <><span className="material-symbols-outlined" style={{ fontSize: 18 }}>send</span> Invia codice di accesso</>
                )}
              </button>
            </form>
          </>
        )}

        {/* OTP step */}
        {(step === "otp" || step === "verifying") && (
          <>
            <p style={{ margin: 0, color: "rgba(255,255,255,0.8)", fontSize: "var(--font-size-base)", textAlign: "center" }}>
              Abbiamo inviato un codice a<br />
              <strong style={{ color: "#fff" }}>{email}</strong>
            </p>
            <form onSubmit={handleVerifyOtp} style={{ display: "flex", flexDirection: "column", gap: "0.75rem", width: "100%" }}>
              <input
                className="form-input"
                type="text"
                inputMode="numeric"
                placeholder="00000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                style={{ fontSize: "1.5rem", textAlign: "center", letterSpacing: "0.3em" }}
                required
                autoFocus
                maxLength={8}
                disabled={step === "verifying"}
              />
              <button type="submit" style={{
                width: "100%", padding: "0.75rem", borderRadius: 8, border: "none",
                background: "#fff", color: "#002855", fontWeight: 600, fontSize: "var(--font-size-base)",
                cursor: "pointer", opacity: step === "verifying" || otp.length < 8 ? 0.6 : 1,
              }} disabled={step === "verifying" || otp.length < 8}>
                {step === "verifying" ? <><span className="spinner" /> Verifica…</> : "Accedi"}
              </button>
              <button type="button" style={{
                width: "100%", padding: "0.5rem", borderRadius: 8, border: "none",
                background: "transparent", color: "rgba(255,255,255,0.7)", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 4, fontSize: "var(--font-size-sm)",
              }} onClick={() => { setStep("email"); setOtp(""); setError(""); }}
                disabled={step === "verifying"}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_back</span>
                Cambia email
              </button>
            </form>
          </>
        )}

        {/* Error */}
        {error && (
          <div style={{
            color: "#fff", background: "rgba(255,255,255,0.1)",
            padding: "0.75rem 1rem", borderRadius: 8, fontSize: "var(--font-size-sm)",
            width: "100%", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>error</span>
            {error}
          </div>
        )}

        {/* Footer */}
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: "0.5rem" }}>
          © Tinexta InfoCert 2026
        </div>
      </div>
    </div>
  );
}
